import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  Sparkles, Plus, Pencil, Save, Trash2, X, Wine, Flower, Candy, Package,
  Coffee, ChevronDown, ChevronRight, DollarSign, Check, ToggleLeft, ToggleRight,
} from "lucide-react";
import { api } from "../../lib/api";
import { F, Skeleton, useConfirm } from "./_shared";

/**
 * AddOnsTab — configurable catalog of chauffeur-booking add-ons.
 *
 * Structure:
 *   Category (Flowers / Candy / Chips / Alcohol / ...)
 *     └─ Items (dozen roses, Snickers, Doritos, Hennessy 375ml, ...)
 *
 * Managers can:
 *   • Create / rename / re-order / activate categories
 *   • Under each category, add / edit / delete individual items with a price
 *   • Toggle item availability without deleting
 *
 * The public `/api/addons/catalog` endpoint returns only active categories
 * with their active items — the chauffeur booking flow reads that live.
 */

const ICONS = { flower: Flower, wine: Wine, candy: Candy, chips: Package, cup: Coffee, sparkles: Sparkles };

export default function AddOnsTab() {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState({}); // category_id -> items[]
  const [loading, setLoading] = useState(true);
  const [openCat, setOpenCat] = useState(null);
  const [editingCat, setEditingCat] = useState(null); // {} = new, {…} = edit
  const [editingItem, setEditingItem] = useState(null); // {category_id, item?}
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/addons/categories");
      setCats(data || []);
      // preload items for first two categories
      const first = (data || [])[0];
      if (first && !items[first.id]) await loadItems(first.id);
      if (first) setOpenCat(first.id);
    } catch (e) {
      toast.error("Failed to load add-ons catalog");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadItems = async (catId) => {
    try {
      const { data } = await api.get(`/admin/addons/items?category_id=${catId}`);
      setItems((cur) => ({ ...cur, [catId]: data || [] }));
    } catch { /* silent — user retries */ }
  };

  const toggleCat = async (c) => {
    if (openCat === c.id) return setOpenCat(null);
    setOpenCat(c.id);
    if (!items[c.id]) await loadItems(c.id);
  };

  const removeCat = async (c) => {
    const ok = await confirm({
      title: `Delete "${c.label}" category?`,
      message: `All ${c.item_count} item(s) inside will be deleted too.`,
      meta: `${c.label} · ${c.slug}`,
      confirmLabel: "Delete category",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/addons/categories/${c.id}`);
      toast.success("Category deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const toggleCatActive = async (c) => {
    try {
      await api.patch(`/admin/addons/categories/${c.id}`, { active: !c.active });
      toast.success(!c.active ? "Category enabled" : "Category disabled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeItem = async (it) => {
    const ok = await confirm({
      title: "Delete this item?",
      meta: `${it.name} · $${it.price.toFixed(2)}`,
      confirmLabel: "Delete item",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/addons/items/${it.id}`);
      toast.success("Item deleted");
      await loadItems(it.category_id);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const toggleItemActive = async (it) => {
    try {
      await api.patch(`/admin/addons/items/${it.id}`, { active: !it.active });
      await loadItems(it.category_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const totalItems = useMemo(
    () => cats.reduce((n, c) => n + (c.item_count || 0), 0),
    [cats]
  );

  return (
    <div data-testid="bo-addons-tab" className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps text-cyan text-xs flex items-center gap-1.5">
            <Sparkles size={12} /> Chauffeur add-ons catalog
          </div>
          <h2 className="font-serif text-2xl text-white mt-1">Categories &amp; items</h2>
          <p className="text-xs text-white/50 mt-1">
            Flowers, candy, chips, alcohol — anything a chauffeured passenger might want in the car.
            Customers see active items during booking.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right text-xs">
            <div className="text-white font-medium">{cats.length} categories · {totalItems} items</div>
            <div className="text-white/40">tap a category to manage its items</div>
          </div>
          <button
            type="button"
            data-testid="bo-addons-new-cat"
            onClick={() => setEditingCat({})}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs label-caps bg-cyan text-midnight font-semibold hover:brightness-110">
            <Plus size={12} /> New category
          </button>
        </div>
      </div>

      {loading ? <Skeleton /> : (
        <div className="space-y-3">
          {cats.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center text-white/50 text-sm">
              No categories yet. Click "New category" to add Flowers, Alcohol, etc.
            </div>
          )}
          {cats.map((c) => {
            const Icon = ICONS[c.icon] || Sparkles;
            const isOpen = openCat === c.id;
            return (
              <div
                key={c.id}
                data-testid={`bo-addon-cat-${c.id}`}
                className={`rounded-2xl border ${c.active ? "border-white/10 bg-white/[0.03]" : "border-white/5 bg-white/[0.01] opacity-70"} overflow-hidden`}>
                <div className="flex items-center gap-3 p-4">
                  <button
                    type="button"
                    onClick={() => toggleCat(c)}
                    data-testid={`bo-addon-cat-toggle-${c.id}`}
                    className="flex items-center gap-3 flex-1 text-left">
                    {isOpen ? <ChevronDown size={16} className="text-white/60" /> : <ChevronRight size={16} className="text-white/60" />}
                    <div className="w-9 h-9 rounded-lg bg-cyan/15 text-cyan flex items-center justify-center">
                      <Icon size={16} />
                    </div>
                    <div>
                      <div className="text-white font-medium">{c.label}</div>
                      <div className="text-[10px] label-caps text-white/40">
                        {c.slug} · {c.item_count} item{c.item_count === 1 ? "" : "s"}
                        {!c.active && " · disabled"}
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => toggleCatActive(c)}
                      data-testid={`bo-addon-cat-toggle-active-${c.id}`}
                      className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/5">
                      {c.active ? <ToggleRight size={16} className="text-lime-300" /> : <ToggleLeft size={16} />}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingCat(c)}
                      data-testid={`bo-addon-cat-edit-${c.id}`}
                      className="p-1.5 rounded-lg text-white/60 hover:text-cyan hover:bg-cyan/10">
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeCat(c)}
                      data-testid={`bo-addon-cat-delete-${c.id}`}
                      className="p-1.5 rounded-lg text-white/40 hover:text-red-300 hover:bg-red-500/10">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-white/5 bg-ink-950/40 p-4 space-y-2">
                    {(items[c.id] || []).length === 0 && (
                      <div className="text-xs text-white/40 italic">No items yet — add one below.</div>
                    )}
                    {(items[c.id] || []).map((it) => (
                      <div key={it.id}
                        data-testid={`bo-addon-item-${it.id}`}
                        className={`rounded-xl border ${it.active ? "border-white/8 bg-white/[0.02]" : "border-white/5 opacity-60"} p-3 flex items-center gap-3`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white text-sm font-medium">{it.name}</span>
                            <span className="text-[11px] font-mono text-lime-300 inline-flex items-center gap-0.5">
                              <DollarSign size={9} />{it.price.toFixed(2)}
                            </span>
                            {!it.active && <span className="text-[9px] label-caps text-white/40">disabled</span>}
                          </div>
                          {it.description && <div className="text-[11px] text-white/50 mt-0.5">{it.description}</div>}
                        </div>
                        <div className="flex items-center gap-1">
                          <button type="button"
                            onClick={() => toggleItemActive(it)}
                            data-testid={`bo-addon-item-toggle-${it.id}`}
                            className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/5">
                            {it.active ? <ToggleRight size={14} className="text-lime-300" /> : <ToggleLeft size={14} />}
                          </button>
                          <button type="button"
                            onClick={() => setEditingItem({ category_id: c.id, item: it })}
                            data-testid={`bo-addon-item-edit-${it.id}`}
                            className="p-1.5 rounded-lg text-white/60 hover:text-cyan hover:bg-cyan/10">
                            <Pencil size={12} />
                          </button>
                          <button type="button"
                            onClick={() => removeItem(it)}
                            data-testid={`bo-addon-item-delete-${it.id}`}
                            className="p-1.5 rounded-lg text-white/40 hover:text-red-300 hover:bg-red-500/10">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setEditingItem({ category_id: c.id, item: null })}
                      data-testid={`bo-addon-item-new-${c.id}`}
                      className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-cyan/40 text-cyan/80 hover:text-cyan hover:bg-cyan/5 py-2.5 text-xs label-caps">
                      <Plus size={12} /> Add {c.label.toLowerCase()} item
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editingCat && (
        <CategoryModal
          category={editingCat.id ? editingCat : null}
          onClose={() => setEditingCat(null)}
          onSaved={() => { setEditingCat(null); load(); }}
        />
      )}
      {editingItem && (
        <ItemModal
          categoryId={editingItem.category_id}
          item={editingItem.item}
          onClose={() => setEditingItem(null)}
          onSaved={async () => {
            const cid = editingItem.category_id;
            setEditingItem(null);
            await loadItems(cid);
            load();
          }}
        />
      )}
      {confirmDialog}
    </div>
  );
}

/* ---------------- Category modal ---------------- */
function CategoryModal({ category, onClose, onSaved }) {
  const [form, setForm] = useState({
    label: category?.label || "",
    slug: category?.slug || "",
    icon: category?.icon || "sparkles",
    sort_order: category?.sort_order ?? 100,
    active: category?.active !== false,
  });
  const [busy, setBusy] = useState(false);
  const isEdit = !!category;
  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return toast.error("Label required");
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/addons/categories/${category.id}`, {
          label: form.label.trim(),
          icon: form.icon,
          sort_order: Number(form.sort_order) || 100,
          active: form.active,
        });
        toast.success("Category updated");
      } else {
        await api.post("/admin/addons/categories", {
          label: form.label.trim(),
          slug: form.slug.trim() || null,
          icon: form.icon,
          sort_order: Number(form.sort_order) || 100,
          active: form.active,
        });
        toast.success("Category added");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <div onClick={onClose} data-testid="bo-addon-cat-modal"
      className="fixed inset-0 z-50 bg-ink-950/85 flex items-center justify-center p-4">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-md bg-ink-900 border border-cyan/40 rounded-3xl p-6 space-y-3">
        <div className="flex items-center justify-between">
          <div className="label-caps text-cyan text-xs">{isEdit ? "Edit category" : "New category"}</div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white p-1"><X size={16} /></button>
        </div>
        <F label="Label *">
          <input required autoFocus data-testid="bo-addon-cat-label"
            value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
            className="input" placeholder="e.g. Cigars" />
        </F>
        {!isEdit && (
          <F label="Slug (auto if blank)">
            <input data-testid="bo-addon-cat-slug"
              value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
              className="input" placeholder="cigars" />
          </F>
        )}
        <div className="grid grid-cols-2 gap-2">
          <F label="Icon">
            <select data-testid="bo-addon-cat-icon"
              value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="input">
              <option value="sparkles">Sparkles</option>
              <option value="flower">Flower</option>
              <option value="wine">Wine</option>
              <option value="candy">Candy</option>
              <option value="chips">Package</option>
              <option value="cup">Beverage</option>
            </select>
          </F>
          <F label="Sort order">
            <input type="number" data-testid="bo-addon-cat-sort"
              value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              className="input" />
          </F>
        </div>
        <label className="flex items-center gap-2 text-xs text-white/70">
          <input type="checkbox" checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
            className="w-4 h-4 accent-cyan" />
          Show this category to customers
        </label>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 py-2.5 rounded-full text-xs label-caps border border-white/12 text-white/60">Cancel</button>
          <button type="submit" disabled={busy} data-testid="bo-addon-cat-save"
            className="flex-1 py-2.5 rounded-full text-xs label-caps bg-cyan text-midnight font-semibold hover:brightness-110 disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
            <Save size={12} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
        <style>{`.input{background:#0f0f11;border:1px solid rgba(255,255,255,0.08);color:#fff;border-radius:10px;padding:10px 12px;width:100%;font-size:13px;}.input:focus{outline:none;border-color:#39D6FF;}`}</style>
      </form>
    </div>
  );
}

/* ---------------- Item modal ---------------- */
function ItemModal({ categoryId, item, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item?.name || "",
    description: item?.description || "",
    price: item?.price ?? "",
    photo_url: item?.photo_url || "",
    active: item?.active !== false,
    sort_order: item?.sort_order ?? 100,
  });
  const [busy, setBusy] = useState(false);
  const isEdit = !!item;
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name required");
    if (form.price === "" || Number.isNaN(Number(form.price))) return toast.error("Valid price required");
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/addons/items/${item.id}`, {
          name: form.name.trim(),
          description: form.description.trim(),
          price: Number(form.price),
          photo_url: form.photo_url.trim(),
          active: form.active,
          sort_order: Number(form.sort_order) || 100,
        });
        toast.success("Item updated");
      } else {
        await api.post("/admin/addons/items", {
          category_id: categoryId,
          name: form.name.trim(),
          description: form.description.trim(),
          price: Number(form.price),
          photo_url: form.photo_url.trim(),
          active: form.active,
          sort_order: Number(form.sort_order) || 100,
        });
        toast.success("Item added");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <div onClick={onClose} data-testid="bo-addon-item-modal"
      className="fixed inset-0 z-50 bg-ink-950/85 flex items-center justify-center p-4">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-md bg-ink-900 border border-cyan/40 rounded-3xl p-6 space-y-3">
        <div className="flex items-center justify-between">
          <div className="label-caps text-cyan text-xs">{isEdit ? "Edit item" : "New item"}</div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white p-1"><X size={16} /></button>
        </div>
        <F label="Name *">
          <input required autoFocus data-testid="bo-addon-item-name"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="input" placeholder="e.g. Hennessy 375ml" />
        </F>
        <F label="Description">
          <input data-testid="bo-addon-item-desc"
            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="input" placeholder="Cognac · optional details" />
        </F>
        <div className="grid grid-cols-2 gap-2">
          <F label="Price ($) *">
            <input required type="number" step="0.01" min="0" data-testid="bo-addon-item-price"
              value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
              className="input" placeholder="45.00" />
          </F>
          <F label="Sort order">
            <input type="number" data-testid="bo-addon-item-sort"
              value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              className="input" />
          </F>
        </div>
        <F label="Photo URL (optional)">
          <input data-testid="bo-addon-item-photo"
            value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
            className="input" placeholder="https://..." />
        </F>
        <label className="flex items-center gap-2 text-xs text-white/70">
          <input type="checkbox" checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
            className="w-4 h-4 accent-cyan" />
          Available for customers to add
        </label>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 py-2.5 rounded-full text-xs label-caps border border-white/12 text-white/60">Cancel</button>
          <button type="submit" disabled={busy} data-testid="bo-addon-item-save"
            className="flex-1 py-2.5 rounded-full text-xs label-caps bg-cyan text-midnight font-semibold hover:brightness-110 disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
            <Save size={12} /> {busy ? "Saving…" : "Save item"}
          </button>
        </div>
        <style>{`.input{background:#0f0f11;border:1px solid rgba(255,255,255,0.08);color:#fff;border-radius:10px;padding:10px 12px;width:100%;font-size:13px;}.input:focus{outline:none;border-color:#39D6FF;}`}</style>
      </form>
    </div>
  );
}
